import React, {RefObject} from "react";
import { observer } from "mobx-react";
import classNames from "classnames";
import { DomEventSubscription } from "../../../../../utils/event-listener-registration";
import { OrbitComponentWithState } from "../../../base-components/orbit-component-with-state/orbit-component-with-state";
import { HeaderFilterConfiguration } from "../../../../services/configurations/config-data-sections/arenas.configuration";
import { arraysEqual } from "../../../../../utils/array-helper";
import { IStickyHandler } from "../../../sticky-handler/sticky-handler.interface";
import { LiveCasinoFilterButtonsBox } from "./boxes/live-casino-filter-buttons/live-casino-filter-buttons.box";
import { LiveCasinoNavbarScrollBox } from "./boxes/live-casino-navbar-scroll/live-casino-navbar-scroll.box";
import { LiveCasinoNavbarOverflowBox } from "./boxes/live-casino-navbar-overflow/live-casino-navbar-overflow.box";
import { STICKY_NAVBAR } from "./css-constants";
import { LiveCasinoNavbarBox } from "./boxes/live-casino-navbar/live-casino-navbar.box";
import { ButtonsActiveIndicatorBox } from "./boxes/buttons-active-indicator/buttons-active-indicator.box";
import {LiveCasinoArenaNavbarButtonProps} from "./live-casino-arena-navbar-button.component";

interface ICasinoLiveArenaNavbarProps {
  arenas: HeaderFilterConfiguration[];
  stickyHandler: IStickyHandler;
  FilterButtonComponent: React.ComponentType<LiveCasinoArenaNavbarButtonProps>;
}

interface ICasinoLiveArenaNavbarState {
  activeButton: HTMLElement | null;
  activeIndicator: {
    width: number;
    position: number;
  };
  currentFilterIndex: number;
}

@observer
export class LiveCasinoArenaNavbar
  extends OrbitComponentWithState<
    ICasinoLiveArenaNavbarProps,
    ICasinoLiveArenaNavbarState
  > {
  private navbarRef = React.createRef<HTMLDivElement>();
  private buttonsRefs: RefObject<HTMLDivElement>[] = [];
  private scrollContainerRef = React.createRef<HTMLDivElement>();

  private _scrollEventHandler: DomEventSubscription | null = null;
  private _resizeEventHandler: DomEventSubscription | null = null;
  private _buttonsRefsFilters: string[] = [];

  constructor(props: ICasinoLiveArenaNavbarProps) {
    super(props);

    this.state = {
      activeButton: null,
      activeIndicator: {
        width: 0,
        position: 0,
      },
      currentFilterIndex: 0,
    };

    // Create a ref for each category button
    this.buttonsRefs = this.props.arenas.map((category) =>
      React.createRef<HTMLDivElement>()
    );
  }

  _getFilterInFirstEnter(){
    const  [activeFilter]  = this.services.gamesFilter.currentFilters.filter(filter => filter.on);

    if (activeFilter && activeFilter.id) {

      const activeFilterButtonIndex = this._buttonsRefsFilters?.findIndex((filterButton) => filterButton === activeFilter.id);

      //Making sure there is associated button with the active filter
      if(activeFilterButtonIndex >= 0){
        this._onFilterClicked(activeFilterButtonIndex, activeFilter.id, false);
      }
    }else{
      this._onFilterClicked(this.state.currentFilterIndex || 0, null, false);
    }
  }

  componentDidMount() {
    super.componentDidMount();

    this._getFilterInFirstEnter();

    if (!this.services.device.isMobile) {
      this._updateActiveIndicatorPosition();
      this._resizeEventHandler = this.services.window.subscribeTo.resize(
        this._updateActiveIndicatorPosition
      );
    }
  }

  componentWillUnmount() {
    this._resizeEventHandler && this._resizeEventHandler.dispose();
    this._scrollEventHandler && this._scrollEventHandler.dispose();
    this.props.stickyHandler && this.props.stickyHandler.dispose();
  }

  private _renderActiveIndicator() {
    if (this.services.device.isMobile) {
      return null;
    }

    return (
      <ButtonsActiveIndicatorBox
        className={"cy-buttons-active-indicator"}
        activeIndicatorWidth={this.state.activeIndicator.width}
        activeIndicatorPosition={this.state.activeIndicator.position}
      ></ButtonsActiveIndicatorBox>
    );
  }

  private _onFilterClicked(filterIndex: number, filter: string, shouldRedirect: boolean = true){
    this._updateStateForFilterChange(filterIndex);

    const category = this.props.arenas[filterIndex];
    this.services.liveTableFeeds.setCurrentFilter({...category, label: category.title});

    filter
      ? this.services.gamesFilter.runFilter(filter)
      : this.services.gamesFilter.clearFilters(shouldRedirect);
  };

  private _updateStateForFilterChange(filterIndex: number): void {
    this.setState({currentFilterIndex: filterIndex}, () => {
        if (!this.services.device.isMobile) {
          // Update the active indicator position
          this._updateActiveIndicatorPosition();
        }
      }
    );
  }

  private _updateActiveIndicatorPosition = () => {
    const activeButton = this.buttonsRefs[this.state.currentFilterIndex]
      .current;
    const scrollContainer = this.scrollContainerRef.current;

    // Use this width offset to make the active indicator larger than the actual button
    const widthOffset = 100;

    if (scrollContainer && activeButton) {
      this.setState({
        activeIndicator: {
          width: activeButton.offsetWidth + widthOffset,
          position:
            activeButton.getBoundingClientRect().left -
            widthOffset / 2 -
            scrollContainer.getBoundingClientRect().left,
        },
      });
    }
  };

  componentWillUpdate(
      nextProps: Readonly<ICasinoLiveArenaNavbarProps>,
      nextState: Readonly<ICasinoLiveArenaNavbarState>,
      nextContext: any
  ): void {
    let newFilters: string[] = [];
    nextProps.arenas.forEach((category, i) => {
      newFilters[i] = category.filter;
    });

    if (!arraysEqual(newFilters, this._buttonsRefsFilters)) {
      this._onFilterClicked(
          this.state.currentFilterIndex,
          newFilters[this.state.currentFilterIndex],
          false
      );
    }
  }

  render() {
    const FilterButtonComponent = this.props.FilterButtonComponent || React.Fragment;

    return (
      <LiveCasinoNavbarBox
        ref={this.navbarRef}
        className={classNames("cy-live-casino-navbar", {
          [STICKY_NAVBAR]: this.props.stickyHandler.isSticky,
        })}
      >
        <LiveCasinoNavbarOverflowBox
          className={"cy-live-casino-navbar-overflow"}
        >
          <LiveCasinoNavbarScrollBox
            className={"cy-live-casino-navbar-scroll"}
            ref={this.scrollContainerRef}
          >
            <LiveCasinoFilterButtonsBox
              className={"cy-live-casino-filter-buttons"}
            >
              {this.props.arenas.map((category, i) => {

                this._buttonsRefsFilters[i] = category.filter;

                return (<FilterButtonComponent
                    key={category.title}
                    label={category.title}
                    filter={category.filter}
                    onClick={() => this._onFilterClicked(i, category.filter)}
                    buttonLabelRef={this.buttonsRefs[i]}
                    seoPage={category.seoPage}
                    isActiveTab={this.state.currentFilterIndex === i}
                />);

              })}
            </LiveCasinoFilterButtonsBox>
            {this._renderActiveIndicator()}
          </LiveCasinoNavbarScrollBox>
        </LiveCasinoNavbarOverflowBox>
      </LiveCasinoNavbarBox>
    );
  }
}
